import {
  type TransactionApiItem,
  type TransactionViewCounterparty,
  TransactionViewDirection,
  type TransactionViewLine,
} from '@/api/transaction-views/queries';
import ArrowRightIcon from '@/assets/svg/ui-elements/arrow-right.svg';
import { defaultSuiNetwork, getSuiExplorerAddressUrl } from '@/config/sui';
import { theme, Typography } from '@/design-system';
import { formatTransactionType } from '@/features/transactions/utils/format-transaction-type';
import {
  formatFee,
  formatTransactionNetwork,
  isGasRebate,
} from '@/features/transactions/utils/transaction-detail';
import { getCounterpartyWalletAddress } from '@/features/transactions/utils/transaction-list';
import { truncateAddress } from '@/utils/truncate-address';

import {
  AddressDisplay,
  AddressLink,
  ExternalIcon,
  Fact,
  FactLabel,
  Facts,
  FactValue,
  SectionLabel,
  TransferArrow,
  TransferEndpoint,
  TransferPath,
  TransferSection,
} from './transaction-detail-modal.styles';

interface TransactionDetailFactsProps {
  detail: TransactionApiItem;
  counterpartyDisplayNames?: ReadonlyMap<string, string>;
}

export const TransactionDetailFacts = ({
  detail,
  counterpartyDisplayNames,
}: TransactionDetailFactsProps) => (
  <>
    {detail.lines.length > 0 ? (
      <TransferSection>
        <SectionLabel as="h3" variant="body4" fontWeight={600}>
          Transfer
        </SectionLabel>
        {detail.lines.map((line, index) => (
          <TransferLinePath
            key={index}
            line={line}
            isOutgoing={isOutgoingLine(line, detail.direction)}
            counterpartyDisplayNames={counterpartyDisplayNames}
          />
        ))}
      </TransferSection>
    ) : null}
    <Facts>
      <DetailFact label="Type" value={formatTransactionType(detail.type)} />
      {detail.onchain ? (
        <DetailFact label="Network" value={formatTransactionNetwork(defaultSuiNetwork)} />
      ) : null}
      {detail.fee ? (
        <DetailFact
          label={isGasRebate(detail.fee) ? 'Gas rebate' : 'Fee'}
          value={formatFee(detail.fee)}
        />
      ) : null}
    </Facts>
  </>
);

const isOutgoingLine = (
  line: TransactionViewLine,
  direction: TransactionViewDirection | null,
): boolean => line.amount?.startsWith('-') ?? direction === TransactionViewDirection.Outgoing;

const DetailFact = ({ label, value }: { label: string; value: string }) => (
  <Fact>
    <FactLabel as="dt" variant="body4" fontWeight={600}>
      {label}
    </FactLabel>
    <FactValue as="dd" variant="body2" fontWeight={500}>
      {value}
    </FactValue>
  </Fact>
);

const TransferLinePath = ({
  line,
  isOutgoing,
  counterpartyDisplayNames,
}: {
  line: TransactionViewLine;
  isOutgoing: boolean;
  counterpartyDisplayNames?: ReadonlyMap<string, string>;
}) => {
  const counterparty = (
    <Counterparty
      counterparty={line.counterparty}
      counterpartyDisplayNames={counterpartyDisplayNames}
    />
  );
  return (
    <TransferPath role="group" aria-label="Transfer path">
      <TransferEndpoint as="dl">
        <FactLabel as="dt" variant="body4" fontWeight={600}>
          From
        </FactLabel>
        <FactValue as="dd" variant="body2" fontWeight={500}>
          {isOutgoing ? 'Your wallet' : counterparty}
        </FactValue>
      </TransferEndpoint>
      <TransferArrow aria-hidden="true">
        <ArrowRightIcon width={16} height={16} />
      </TransferArrow>
      <TransferEndpoint as="dl">
        <FactLabel as="dt" variant="body4" fontWeight={600}>
          To
        </FactLabel>
        <FactValue as="dd" variant="body2" fontWeight={500}>
          {isOutgoing ? counterparty : 'Your wallet'}
        </FactValue>
      </TransferEndpoint>
    </TransferPath>
  );
};

const Counterparty = ({
  counterparty,
  counterpartyDisplayNames,
}: {
  counterparty: TransactionViewCounterparty | null;
  counterpartyDisplayNames?: ReadonlyMap<string, string>;
}) => {
  if (!counterparty) {
    return <>Unknown</>;
  }
  const walletAddress = getCounterpartyWalletAddress(counterparty);
  const displayName =
    (walletAddress ? counterpartyDisplayNames?.get(walletAddress) : undefined) ?? counterparty.name;
  const content = (
    <AddressDisplay>
      {displayName ? (
        <Typography as="span" variant="body2" fontWeight={500} noWrap>
          {displayName}
        </Typography>
      ) : null}
      <Typography
        as="span"
        variant={displayName ? 'body4' : 'body2'}
        color={displayName ? theme.colors.text.secondary : theme.colors.text.primary}
        noWrap
      >
        {truncateAddress(counterparty.id)}
      </Typography>
    </AddressDisplay>
  );

  return walletAddress ? (
    <AddressLink
      href={getSuiExplorerAddressUrl(walletAddress)}
      target="_blank"
      rel="noopener noreferrer"
      size="mini"
      aria-label={counterparty.id}
    >
      {content}
      <ExternalIcon width={12} height={12} aria-hidden="true" />
    </AddressLink>
  ) : (
    content
  );
};
